import { useEffect, useState } from 'preact/hooks';
import {
  MAX_RFQ_SUPPLIERS,
  QUOTES_EXPECTED,
  RFQ_TEXT,
  rfqDate,
  unitText,
  type RfqChannel,
  type RfqPageBody,
  type RfqSentBody,
} from '../../shared/rfq/rfq';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend, apiSendForBytes, saveBytes } from '../lib/api';
import { formatDate } from '../lib/dates';

// Request for quotation (screen 09, P2.6). Procurement picks approved suppliers, sets the due
// date and a note, and sends by email (one message per supplier), WhatsApp or print. The
// document shows items and delivery, never the budget; the broker builds what is sent.

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: RfqPageBody };

function RfqDocument({ data, due, note }: { data: RfqPageBody; due: string; note: string }) {
  const { doc } = data;
  return (
    <div class="rfq-doc">
      <img src="/brand/smc-group.svg" alt="SMC Group" width="77" height="30" />
      <div class="rfq-doc__cols">
        {(['en', 'ar'] as const).map((lang) => {
          const t = RFQ_TEXT[lang];
          return (
            <section key={lang} lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'} class="stack">
              <h3 class="rfq-doc__title">
                {t.heading} · <span dir="ltr">{doc.rfqNo}</span>
              </h3>
              <p>
                {t.greeting} {t.intro(rfqDate(due, lang))}
              </p>
              <ul>
                {doc.lines.map((l, i) => (
                  <li key={i}>
                    <span dir="ltr">{l.qty}</span> × {unitText(l.unit, lang)} ·{' '}
                    <span dir="auto">
                      {l.description}
                      {l.specs && <span class="caption"> ({l.specs})</span>}
                    </span>
                  </li>
                ))}
              </ul>
              <p>
                <strong>{t.deliverTo}:</strong> {doc.deliverTo}
                {doc.neededBy && ` · ${t.neededBy} ${rfqDate(doc.neededBy, lang)}`}
              </p>
              {note && (
                <p class="notice notice--info" dir="auto">
                  {t.note}: {note}
                </p>
              )}
              <p class="caption">{t.reply}</p>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function RfqPage({ prNo }: { prNo: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [picked, setPicked] = useState<string[]>([]);
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string; details?: string[] } | null>(
    null,
  );
  const [fields, setFields] = useState<Record<string, string>>({});
  const [links, setLinks] = useState<{ name: string; href: string }[]>([]);

  useEffect(() => {
    let live = true;
    apiGet<RfqPageBody>(`/api/prs/${encodeURIComponent(prNo)}/rfq`)
      .then((data) => {
        if (!live) return;
        setLoad({ kind: 'ok', data });
        setDue((d) => d || data.doc.dueDate);
        setNote((n) => n || data.doc.note);
      })
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [prNo, reload]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'forbidden' ? t('review.error.forbidden') : t('req.loadError')}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }

  const { data } = load;
  const byId = new Map(data.suppliers.map((s) => [s.supplierId, s]));
  const visible = data.suppliers.filter(
    (s) => showAll || s.inCategory || picked.includes(s.supplierId),
  );
  const chosen = picked.map((id) => byId.get(id)).filter((s) => !!s);
  const category = data.category
    ? lang === 'ar'
      ? data.category.nameAR || data.category.nameEN
      : data.category.nameEN
    : '—';
  const toggle = (id: string) =>
    setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);

  async function send(channel: RfqChannel) {
    setResult(null);
    setFields({});
    setLinks([]);
    // Say up front which supplier can't be reached this way, by name (D86).
    const unreachable = chosen.filter((s) =>
      channel === 'email' ? !s.hasEmail : channel === 'whatsapp' ? !s.whatsApp : false,
    );
    if (unreachable.length) {
      setResult({
        ok: false,
        text: t(channel === 'email' ? 'rfq.error.noEmail' : 'rfq.error.noWhatsApp', {
          names: unreachable.map((s) => s.name).join(', '),
        }),
      });
      return;
    }
    setBusy(true);
    try {
      const out = await apiSend<RfqSentBody>('POST', `/api/prs/${encodeURIComponent(prNo)}/rfq`, {
        suppliers: picked,
        channel,
        dueDate: due,
        note,
      });
      const failed = out.sent.filter((s) => channel === 'email' && !s.emailed);
      const done = out.sent.length - failed.length;
      setResult({
        ok: failed.length === 0,
        text:
          t(`rfq.done.${channel}` as MessageKey, { n: String(done) }) +
          (failed.length
            ? ` ${t('rfq.done.failed', { names: failed.map((f) => byId.get(f.supplierId)?.name ?? f.supplierId).join(', ') })}`
            : ''),
      });
      if (channel === 'whatsapp') {
        setLinks(
          out.sent
            .filter((s) => s.whatsapp)
            .map((s) => ({ name: byId.get(s.supplierId)?.name ?? s.supplierId, href: s.whatsapp })),
        );
      }
      if (channel === 'print') setTimeout(() => window.print(), 50);
      setPicked([]);
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      const f = e?.fields ?? {};
      setFields(f);
      const known = ['invalid_input', 'invalid_transition', 'not_configured', 'forbidden'];
      // Every reason the broker gave, in words, so nothing is left unexplained (D86).
      const details = Object.entries(f).map(([k, v]) => {
        const label = k.startsWith('suppliers')
          ? t('rfq.field.suppliers')
          : k === 'dueDate'
            ? t('rfq.field.dueDate')
            : k === 'note'
              ? t('rfq.field.note')
              : k;
        return `${label}: ${v}`;
      });
      setResult({
        ok: false,
        text:
          t(
            (known.includes(e?.code ?? '')
              ? `rfq.error.${e?.code ?? ''}`
              : 'review.error.generic') as MessageKey,
          ) + (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
        details,
      });
    } finally {
      setBusy(false);
    }
  }

  // The RFQ as a PDF (D88): the phone's or Windows' share sheet with the file attached
  // (WhatsApp, Outlook…), or a download where the browser can't share files.
  async function sharePdf(mode: 'share' | 'download') {
    setPdfBusy(true);
    setResult(null);
    try {
      const out = await apiSendForBytes(`/api/prs/${encodeURIComponent(prNo)}/rfq/pdf`, {
        dueDate: due,
        note,
      });
      const file = new File([out.bytes as Uint8Array<ArrayBuffer>], out.name, {
        type: 'application/pdf',
      });
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
      if (mode === 'share' && nav.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: data.doc.rfqNo });
          setResult({ ok: true, text: t('rfq.pdf.shared') });
        } catch (e) {
          // Closing the share sheet is not an error.
          if (!(e instanceof DOMException && e.name === 'AbortError')) throw e;
        }
      } else {
        saveBytes(out.bytes, out.name, 'application/pdf');
        setResult({
          ok: true,
          text: t(mode === 'share' ? 'rfq.pdf.downloadedShare' : 'rfq.pdf.downloaded'),
        });
      }
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setResult({
        ok: false,
        text:
          t(e?.code === 'pdf_failed' ? 'rfq.pdf.failed' : 'review.error.generic') +
          (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
        details: Object.entries(e?.fields ?? {}).map(([k, v]) =>
          k === 'dueDate' ? `${t('rfq.field.dueDate')}: ${v}` : `${k}: ${v}`,
        ),
      });
    } finally {
      setPdfBusy(false);
    }
  }

  const cantSend = busy || !data.canSend || picked.length === 0;

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/queue">{t('nav.queue')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>{' '}
        / RFQ · <a href={`#/requests/${prNo}/quotes`}>{t('quote.open')}</a>
      </p>
      <p>
        <strong dir="auto">{data.doc.title}</strong> ·{' '}
        {t('rfq.pickHint', { n: String(QUOTES_EXPECTED) })}
      </p>
      {!data.canSend && <p class="notice notice--action">{t('rfq.notSourcing')}</p>}
      {result && (
        <div
          class={result.ok ? 'form-saved' : 'form-problem'}
          role={result.ok ? 'status' : 'alert'}
        >
          <p>{result.text}</p>
          {result.details && result.details.length > 0 && (
            <ul>
              {result.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {links.length > 0 && (
        <section class="card" aria-labelledby="rfq-wa">
          <h3 id="rfq-wa" class="card__title">
            {t('rfq.waTitle')}
          </h3>
          <ul class="action-list">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  class="button button--small"
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('rfq.waOpen', { name: l.name })}
                </a>
              </li>
            ))}
          </ul>
          <p class="caption">{t('rfq.pdf.waHint')}</p>
          <div class="actions">
            <button
              type="button"
              class="button button--primary"
              disabled={pdfBusy}
              onClick={() => void sharePdf('share')}
            >
              {t('rfq.pdf.share')}
            </button>
          </div>
        </section>
      )}

      <div class="rfq-layout">
        <section class="card rfq-side" aria-labelledby="rfq-suppliers">
          <div class="card__head">
            <h3 id="rfq-suppliers" class="card__title">
              {t('rfq.suppliers', { category })}
            </h3>
            <span class={`pill pill--${picked.length >= QUOTES_EXPECTED ? 'success' : 'info'}`}>
              {t('rfq.selected', { n: String(picked.length) })}
            </span>
          </div>
          {visible.length === 0 ? (
            <p>{t('rfq.noSuppliers')}</p>
          ) : (
            <ul class="rfq-suppliers">
              {visible.map((s) => (
                <li key={s.supplierId}>
                  <input
                    id={`rfq-${s.supplierId}`}
                    type="checkbox"
                    checked={picked.includes(s.supplierId)}
                    disabled={!picked.includes(s.supplierId) && picked.length >= MAX_RFQ_SUPPLIERS}
                    onChange={() => toggle(s.supplierId)}
                  />
                  <label for={`rfq-${s.supplierId}`}>
                    <span class="req-row__title">{s.name}</span>
                    <span class="caption cell-sub">
                      {[s.city, s.country ? t(`reg.country.${s.country}` as MessageKey) : '']
                        .filter(Boolean)
                        .join(', ')}
                      {!s.inCategory && ` · ${t('rfq.otherCategory')}`}
                      {!s.hasEmail && (
                        <>
                          {' · '}
                          <span class="rfq-warn">{t('rfq.noEmailTag')}</span>
                        </>
                      )}
                      {s.sent &&
                        ` · ${t(`rfq.sent.${s.sent.channel}` as MessageKey, { date: formatDate(s.sent.sentAt, lang) })}`}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {fields.suppliers && <p class="field__error">{fields.suppliers}</p>}
          <div class="field field--check">
            <input
              id="rfq-all"
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll((e.target as HTMLInputElement).checked)}
            />
            <label for="rfq-all">{t('rfq.showAll')}</label>
          </div>
          <a href="#/suppliers">{t('rfq.invite')}</a>
        </section>

        <div class="stack">
          <section class="card" aria-labelledby="rfq-details">
            <h3 id="rfq-details" class="visually-hidden">
              {t('rfq.details')}
            </h3>
            <div class="reg-grid">
              <div class="field">
                <label for="rfq-due">{t('rfq.due')}</label>
                <input
                  id="rfq-due"
                  type="date"
                  dir="ltr"
                  value={due}
                  aria-invalid={fields.dueDate ? true : undefined}
                  aria-describedby="rfq-due-when"
                  onInput={(e) => setDue((e.target as HTMLInputElement).value)}
                />
                <DateHint id="rfq-due-when" value={due} />
                {fields.dueDate && <p class="field__error">{fields.dueDate}</p>}
              </div>
              <div class="field">
                <span class="field__label">{t('rfq.deliverTo')}</span>
                <p class="rfq-static">
                  {data.doc.deliverTo}
                  {data.doc.neededBy &&
                    ` · ${t('rfq.by', { date: formatDate(data.doc.neededBy, lang) })}`}
                </p>
              </div>
            </div>
            <div class="field">
              <label for="rfq-note">{t('rfq.note')}</label>
              <textarea
                id="rfq-note"
                rows={2}
                dir="auto"
                maxLength={1000}
                value={note}
                onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
              />
            </div>
          </section>

          <section class="card" aria-labelledby="rfq-preview">
            <div class="card__head">
              <h3 id="rfq-preview" class="card__title">
                {t('rfq.preview')}
              </h3>
              <span class="caption" dir="ltr">
                {data.from ? t('rfq.from', { from: data.from }) : ''}
              </span>
            </div>
            <RfqDocument data={data} due={due} note={note} />
            <p class="caption">{t('rfq.noBudget')}</p>
          </section>

          <div class="actions rfq-actions">
            <button
              type="button"
              class="button"
              disabled={pdfBusy || !data.canSend}
              onClick={() => void sharePdf('download')}
            >
              {t('rfq.pdf.download')}
            </button>
            <button
              type="button"
              class="button"
              disabled={pdfBusy || !data.canSend}
              onClick={() => void sharePdf('share')}
            >
              {t('rfq.pdf.share')}
            </button>
            <button
              type="button"
              class="button"
              disabled={cantSend}
              onClick={() => void send('print')}
            >
              {t('rfq.print')}
            </button>
            <button
              type="button"
              class="button"
              disabled={cantSend}
              onClick={() => void send('whatsapp')}
            >
              {t('rfq.whatsapp')}
            </button>
            <button
              type="button"
              class="button button--primary"
              disabled={cantSend}
              onClick={() => void send('email')}
            >
              {t('rfq.email', { n: String(chosen.length) })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
